import React, { useEffect, useState, useRef } from "react";
import MessageBroker from "@bonprix/pattern-library/components/message-broker";
import Messages from "@bonprix/pattern-library/components/messages";
import SymbolButton from "@bonprix/pattern-library/templates/symbol-button/default";

const BUTTON_WIDTH = 20;

export default function MiniSlider({
  className,
  children,
  isTouch,
  onPrevButtonClick,
  onNextButtonClick,
  onFirstSlide,
  activeSlideIndex,
  attributes,
  testRefs,
  beyondEdgeSliding,
  fixedSlideSize,
}) {
  const [activeBulletIndex, setActiveBulletIndex] = useState(0);
  const [slideGroup, setSlideGroup] = useState(0);
  const [onFirstSlideActionExecuted, setOnFirstSlideActionExecuted] =
    useState(false);
  const windowWidth = useRef(window.innerWidth);

  const contentWrapperRef = useRef();
  const contentRef = useRef();
  const childRef = useRef();

  useEffect(() => {
    const subscription = MessageBroker.subscribe(
      Messages.WINDOW.RESIZED,
      ({ width }) => {
        if (
          width <= windowWidth.current - 15 ||
          width >= windowWidth.current + 15
        ) {
          windowWidth.current = width;
          setActiveBulletIndex(0);
          setSlideGroup(calculateSlideStep());
        }
      }
    );

    return () => MessageBroker.unsubscribe(subscription);
  }, []);

  useEffect(() => {
    if (!isTouch) {
      let nextXPosition;
      let newSlideNode;
      let newSlideIndex;

      if (activeBulletIndex === 0) {
        scrollContentTo(0);
        return;
      }

      if (children?.length === slideGroup) {
        scrollContentTo(contentRef.current.scrollWidth);
        return;
      }

      if (children?.length > slideGroup && contentRef) {
        newSlideIndex = activeBulletIndex * slideGroup;
        const lastVisibleSlideIndex = newSlideIndex + slideGroup;
        const childrenNodes = Array.from(contentRef.current.children).filter(
          (childNode) => !childNode.classList.contains("-placeholder")
        );

        if (lastVisibleSlideIndex >= children.length) {
          newSlideIndex = children.length - 1 - slideGroup;
          newSlideNode = childrenNodes[newSlideIndex];
          nextXPosition = contentRef.current.scrollWidth;
        } else {
          newSlideNode = childrenNodes[newSlideIndex];
          const offsetFactor = 0.66; // offset over left edge should be 66% of slide
          const offset = beyondEdgeSliding
            ? newSlideNode.offsetWidth * offsetFactor
            : BUTTON_WIDTH - 10;
          nextXPosition = newSlideNode ? newSlideNode.offsetLeft - offset : 0;
        }

        if (newSlideNode) {
          scrollContentTo(nextXPosition);
        }
      }
    }
  }, [activeBulletIndex]);

  useEffect(() => {
    if (isTouch && activeSlideIndex) {
      const activeElement = contentRef.current.children[activeSlideIndex];
      let contentWidth = 0;
      for (let i = 0; i < activeSlideIndex; i++) {
        const el = contentRef.current.children[i];
        const elStyle = getComputedStyle(el);
        const isLastElement = i === contentRef.current.children.length - 1;
        const elemWidth =
          parseFloat(elStyle.width) +
          (isLastElement ? 0 : parseFloat(elStyle.marginRight));
        contentWidth += elemWidth;
      }
      scrollContentTo(
        contentWidth - window.innerWidth / 2 + activeElement.offsetWidth / 2
      );
    }
  }, [activeSlideIndex]);

  useEffect(() => {
    const slideStep = calculateSlideStep();
    setSlideGroup(slideStep);
    const bulletIndex = Math.ceil((activeSlideIndex + 1) / slideStep) - 1;
    setActiveBulletIndex(bulletIndex >= 0 ? bulletIndex : 0);
  }, [children]);

  const childrenWithProps = children.map((child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        ref: index === 0 ? childRef : null,
        className: "mini-slider__slide",
        key: index,
      });
    }
    return child;
  });

  function calculateSlideStep() {
    if (fixedSlideSize && beyondEdgeSliding) {
      return fixedSlideSize;
    }

    let contentWrapperWidth = getContentWrapperWidth(contentWrapperRef.current);
    if (!isTouch) {
      contentWrapperWidth -= BUTTON_WIDTH;
    }
    let slideStep = 0;

    if (contentRef.current) {
      let contentWidth = 0;
      for (
        let i = activeBulletIndex;
        i < contentRef.current.children.length;
        i++
      ) {
        const el = contentRef.current.children[i];
        const elStyle = getComputedStyle(el);
        const isLastElement = i === contentRef.current.children.length - 1;
        const elemWidth =
          parseFloat(elStyle.width) +
          (isLastElement ? 0 : parseFloat(elStyle.marginRight));
        if (contentWidth + elemWidth < contentWrapperWidth) {
          slideStep++;
        }
        contentWidth += elemWidth;
      }
    }

    return slideStep;
  }

  function onSliderButtonClick(isSlideForward) {
    isSlideForward
      ? setActiveBulletIndex(Math.min(activeBulletIndex + 1, maxBulletCount))
      : setActiveBulletIndex(Math.max(activeBulletIndex - 1, 0));

    if (isSlideForward) {
      onNextButtonClick?.();
    } else {
      onPrevButtonClick?.();
    }
  }

  function handleTouchMove() {
    if (onFirstSlide && !onFirstSlideActionExecuted) {
      onFirstSlide();
      setOnFirstSlideActionExecuted(true);
    }
  }

  function getContentWrapperWidth(contentWrapperNode) {
    return parseFloat(getComputedStyle(contentWrapperNode)?.width);
  }

  function scrollContentTo(x) {
    if (contentRef && typeof contentRef.current?.scroll === "function") {
      contentRef.current.scroll({
        left: x,
        behavior: "smooth",
      });
    }
  }

  const itemCount = children?.length;
  const maxBulletCount = slideGroup ? Math.ceil(itemCount / slideGroup) : 1;

  return (
    <div
      className={className}
      {...attributes}
      {...testRefs}
      ref={contentWrapperRef}
    >
      <div
        className="mini-slider__content"
        onTouchMove={handleTouchMove}
        ref={contentRef}
      >
        {beyondEdgeSliding && (
          <div className="mini-slider__slide -placeholder"></div>
        )}
        {childrenWithProps}
      </div>
      {activeBulletIndex !== 0 && !isTouch && (
        <div
          onClick={() => onSliderButtonClick(false)}
          className="mini-slider__button -prev"
        >
          <SymbolButton
            className="mini-slider__symbol-button -prev"
            icon="arrow-left"
          />
        </div>
      )}
      {activeBulletIndex !== maxBulletCount - 1 && !isTouch && (
        <div
          onClick={() => onSliderButtonClick(true)}
          className="mini-slider__button -next"
        >
          <SymbolButton
            className="mini-slider__symbol-button -next"
            icon="arrow-right"
          />
        </div>
      )}
    </div>
  );
}
